import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";

import { redirectToOAuthUrl } from "shared-frontend/functions/auth";
import getEnv from "shared-frontend/functions/getEnv";
// Components.
import LoginForm from "./LoginForm";
import { useTurnstileSubmit } from "./useTurnstileSubmit";
import { ErrorPropTypeShape } from "../../errors/ErrorDisplay";

const TURNSTILE_SITE_KEY = getEnv( "TURNSTILE_SITE_KEY", "1x00000000000000000000AA" );

/**
 * Login component for logging in to MyYoast.
 *
 * @param {object} props The component props.
 *
 * @returns {ReactElement} The rendered component.
 */
const Login = ( { error, loading, loggedIn, oauthError, requireOTP, attemptLogin, attemptPasskeyLogin = () => {} } ) => {
	const [ email, setEmail ] = useState( "" );
	const [ password, setPassword ] = useState( "" );
	const [ rememberMe, setRememberMe ] = useState( false );
	const [ otp, setOtp ] = useState( "" );

	// Handle OAuth redirect when logged in with OAuth error.
	useEffect( () => {
		if ( oauthError && loggedIn ) {
			redirectToOAuthUrl();
		}
	}, [ oauthError, loggedIn ] );

	// Single submit channel shared by the password form and the passkey button: the
	// useTurnstileSubmit hook resolves a token (immediately or once the widget initializes)
	// and then routes to the matching action. Passkey submits are tagged so they dispatch the
	// passkey login instead of the password login.
	const onLoginSubmit = useCallback( ( payload, token ) => {
		if ( payload.passkey ) {
			attemptPasskeyLogin( { turnstileToken: token } );
			return;
		}
		attemptLogin( { ...payload, turnstileToken: token } );
	}, [ attemptLogin, attemptPasskeyLogin ] );

	const {
		turnstileRef,
		turnstileBypassed,
		turnstileVerified,
		turnstileError,
		turnstilePending,
		onTurnstileVerify,
		onTurnstileExpire,
		onTurnstileError,
		onTurnstileTimeout,
		onTurnstileScriptError,
		requestSubmit,
	} = useTurnstileSubmit( { onSubmit: onLoginSubmit, error } );

	const onRememberCheck = useCallback( ( event ) => {
		setRememberMe( event.target.checked );
	}, [] );

	const onUpdateEmail = useCallback( ( event ) => {
		setEmail( event.target.value );
	}, [] );

	const onUpdatePassword = useCallback( ( event ) => {
		setPassword( event.target.value );
	}, [] );

	const onUpdateOTP = useCallback( ( event ) => {
		setOtp( event.target.value );
	}, [] );

	const handleSubmit = useCallback( ( event ) => {
		event.preventDefault();
		if ( loading || ! email || ! password ) {
			return;
		}
		// When Turnstile is bypassed (automated testing), ignore the pending/error guards so the
		// form submits without waiting for the widget.
		if ( ! turnstileBypassed && ( turnstilePending || turnstileError ) ) {
			return;
		}
		requestSubmit( { email, password, rememberMe, otp } );
	}, [ loading, turnstileBypassed, turnstilePending, email, password, turnstileError, requestSubmit, rememberMe, otp ] );

	const handlePasskeyLogin = useCallback( () => {
		if ( loading || turnstilePending || turnstileError ) {
			return;
		}
		requestSubmit( { passkey: true } );
	}, [ loading, turnstilePending, turnstileError, requestSubmit ] );

	return (
		<LoginForm
			rememberMe={ rememberMe }
			email={ email }
			password={ password }
			error={ error }
			loading={ loading }
			turnstileBypassed={ turnstileBypassed }
			turnstilePending={ turnstilePending }
			onUpdateEmail={ onUpdateEmail }
			onUpdatePassword={ onUpdatePassword }
			onUpdateOTP={ onUpdateOTP }
			onRememberCheck={ onRememberCheck }
			handleSubmit={ handleSubmit }
			onPasskeyLogin={ handlePasskeyLogin }
			requireOTP={ requireOTP }
			turnstileSiteKey={ TURNSTILE_SITE_KEY }
			turnstileRef={ turnstileRef }
			turnstileError={ turnstileError }
			turnstileVerified={ turnstileVerified }
			onTurnstileVerify={ onTurnstileVerify }
			onTurnstileExpire={ onTurnstileExpire }
			onTurnstileError={ onTurnstileError }
			onTurnstileTimeout={ onTurnstileTimeout }
			onTurnstileScriptError={ onTurnstileScriptError }
		/>
	);
};

Login.propTypes = {
	error: ErrorPropTypeShape,
	loading: PropTypes.bool,
	loggedIn: PropTypes.bool,
	oauthError: PropTypes.bool,
	requireOTP: PropTypes.bool,
	attemptLogin: PropTypes.func.isRequired,
	attemptPasskeyLogin: PropTypes.func,
};

Login.defaultProps = {
	loggedIn: false,
	loading: false,
	requireOTP: false,
	error: null,
	oauthError: false,
};

export default Login;
